import { useMemo } from 'react'
import * as THREE from 'three'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { CORES_AUTOMOTIVAS } from '../../dominio/didatico/tokens'
import { TransformacaoPeca } from './contextoDeformacao'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

interface Props {
  perfil: PerfilVeiculo
  aceso?: boolean
}

/**
 * Conjunto óptico dianteiro com lentes curvas, projetores LED e indicadores de seta.
 */
export function FaroisVeiculo({ perfil, aceso = true }: Props) {
  const larguraFarol = 0.32
  const alturaFarol = 0.13
  const profundidade = 0.16
  const y = perfil.capoY - alturaFarol / 2 - 0.02
  const z = perfil.frenteZ - profundidade / 2 + 0.01
  const xAfastamento = perfil.largura / 2 - larguraFarol / 2 - 0.06

  return (
    <group>
      {[-1, 1].map((sinal) => (
        // na colisão, o farol recua, gira com a chapa em volta e, no severo, some
        <TransformacaoPeca key={sinal} pecaId={`farol${sinal > 0 ? 'Esquerda' : 'Direita'}`} ancora={[sinal * xAfastamento, y, z]}>
        <group
          position={[sinal * xAfastamento, y, z]}
          rotation={[0.04, -sinal * 0.12, 0]}
        >
          {/* Carcaça / fundo escuro do conjunto óptico */}
          <mesh position={[0, 0, -0.02]}>
            <CaixaArredondada args={[larguraFarol, alturaFarol, profundidade]} />
            <meshStandardMaterial color="#0b0f14" roughness={0.8} metalness={0.2} />
          </mesh>

          {/* Refletor parabólico interno cromado */}
          <mesh position={[-sinal * 0.03, 0, 0.02]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.048, 0.035, 0.04, 16]} />
            <meshStandardMaterial color={CORES_AUTOMOTIVAS.farolRefletor} roughness={0.1} metalness={0.95} />
          </mesh>

          {/* Canhão do projetor LED principal */}
          <mesh position={[-sinal * 0.03, 0, 0.04]}>
            <sphereGeometry args={[0.034, 16, 16]} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.farolProjetor}
              emissive={CORES_AUTOMOTIVAS.farolEmissivo}
              emissiveIntensity={aceso ? 0.9 : 0.2}
              roughness={0.15}
            />
          </mesh>

          {/* Guia de luz DRL (daytime running light) horizontal */}
          <mesh position={[0, alturaFarol * 0.32, 0.055]}>
            <CaixaArredondada args={[larguraFarol * 0.78, 0.015, 0.02]} />
            <meshStandardMaterial
              color="#ffffff"
              emissive="#e0f2fe"
              emissiveIntensity={aceso ? 0.75 : 0.1}
            />
          </mesh>

          {/* Indicador de direção / seta âmbar na extremidade externa */}
          <mesh position={[sinal * (larguraFarol * 0.36), -0.01, 0.045]}>
            <CaixaArredondada args={[larguraFarol * 0.22, alturaFarol * 0.55, 0.03]} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.farolPisca}
              emissive={CORES_AUTOMOTIVAS.farolPisca}
              emissiveIntensity={aceso ? 0.35 : 0.05}
              roughness={0.3}
            />
          </mesh>

          {/* Lente externa de policarbonato translúcido (otimizada para 60 FPS) */}
          <mesh position={[0, 0, 0.065]}>
            <CaixaArredondada args={[larguraFarol + 0.01, alturaFarol + 0.01, 0.015]} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.farolLente}
              roughness={0.08}
              metalness={0.1}
              transparent
              opacity={0.46}
              depthWrite={false}
            />
          </mesh>
        </group>
        </TransformacaoPeca>
      ))}
    </group>
  )
}
